import { data, Link, useLoaderData } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { type SeoProps } from '#app/seo/seo.tsx'
import {
	buildFaqLd,
	buildHowToLd,
	buildWebApplicationLd,
	buildAggregateOfferLd,
} from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/dumpster-size-calculator.ts'
import { DumpsterCalculator } from './components/dumpster-calculator.tsx'
import { DumpsterFaq } from './components/dumpster-faq.tsx'
import { DumpsterPermitGuide } from './components/dumpster-permit-guide.tsx'
import { DumpsterSizeGuide } from './components/dumpster-size-guide.tsx'
import { VendorComparisonTable } from './components/vendor-comparison-table.tsx'
import { DUMPSTER_FAQ_ITEMS } from './dumpster-size-calculator.faq.ts'

export async function loader({}: Route.LoaderArgs) {
	const listings = await prisma.rentalListing.findMany({
		where: {
			status: 'published',
			categories: { some: { slug: 'dumpster-and-waste-rental' } },
		},
		select: {
			id: true,
			title: true,
			slug: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			attributes: true,
			updatedAt: true,
			subcategories: { select: { slug: true, name: true } },
			cities: { select: { id: true, name: true, slug: true } },
			ownerBusiness: {
				select: {
					name: true,
					slug: true,
					phone: true,
					website: true,
				},
			},
		},
		orderBy: { dailyRateCents: 'asc' },
	})

	// Derive freshness date from the most recently updated listing
	const lastUpdated = listings.reduce<Date | null>((latest, l) => {
		const d = new Date(l.updatedAt)
		return !latest || d > latest ? d : latest
	}, null)

	return data({
		listings,
		lastUpdated: lastUpdated?.toISOString() ?? new Date().toISOString(),
	})
}

export const handle = {
	breadcrumb() {
		return {
			name: 'Dumpster Size & Cost Calculator',
			url: '/tools/dumpster-size-calculator',
		}
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const canonical = `${origin}/tools/dumpster-size-calculator`
		const title =
			'DFW Dumpster Size & Cost Calculator | Compare Dallas-Fort Worth Prices'
		const description =
			'Find the right dumpster size for your project and compare prices from Dallas-Fort Worth vendors. Free calculator with real DFW pricing for 10 to 40-yard dumpsters.'

		const structuredData: Array<object | string> = []

		// FAQ schema
		structuredData.push(
			buildFaqLd(
				DUMPSTER_FAQ_ITEMS as unknown as Array<{ q: string; a: string }>,
			),
		)

		// WebApplication schema
		structuredData.push(
			buildWebApplicationLd({
				name: 'DFW Dumpster Size & Cost Calculator',
				url: canonical,
				description,
			}),
		)

		// HowTo schema
		structuredData.push(
			buildHowToLd({
				name: 'How to Choose the Right Dumpster Size in Dallas-Fort Worth',
				description:
					'A step-by-step guide to selecting and renting the right dumpster for your project in the DFW area.',
				steps: [
					{
						name: 'Estimate your debris volume',
						text: 'Consider your project type (remodel, cleanout, roofing) and scope (number of rooms, square footage). A bathroom remodel produces 2-4 cubic yards; a whole-home cleanout can produce 15-20 cubic yards.',
					},
					{
						name: 'Choose the right dumpster size',
						text: 'Match your estimated volume to a dumpster size: 10-yard for small projects, 15-20 yard for medium renovations and cleanouts, 30-40 yard for large construction or commercial projects.',
					},
					{
						name: 'Account for debris weight',
						text: 'Heavy materials like concrete, brick, and shingles hit weight limits before volume limits. For heavy debris, choose a smaller dumpster with adequate weight allowance rather than a larger one.',
					},
					{
						name: 'Compare DFW vendors and pricing',
						text: 'Compare prices from Dallas-Fort Worth dumpster rental companies. Prices vary by vendor, dumpster size, rental period, and delivery location within DFW.',
					},
					{
						name: 'Book your dumpster rental',
						text: 'Contact your chosen vendor to confirm availability, delivery date, and placement location. Check if you need a permit for street placement in your city (Dallas, Fort Worth, Arlington, etc.).',
					},
				],
			}),
		)

		// AggregateOffer from listing prices
		const listings = loaderData?.listings ?? []
		const prices = listings
			.map(
				(l: {
					weeklyRateCents?: number | null
					dailyRateCents?: number | null
				}) => l.weeklyRateCents ?? l.dailyRateCents,
			)
			.filter((p: number | null | undefined): p is number => p != null && p > 0)

		if (prices.length > 0) {
			structuredData.push(
				buildAggregateOfferLd({
					name: 'Dumpster Rental in Dallas-Fort Worth',
					lowPrice: Math.min(...prices) / 100,
					highPrice: Math.max(...prices) / 100,
					offerCount: prices.length,
				}),
			)
		}

		return {
			title,
			description,
			canonical,
			keywords: [
				'dumpster rental dallas',
				'dumpster size calculator',
				'dumpster sizes chart',
				'10 yard dumpster',
				'dumpster rental prices',
				'dumpster rental fort worth',
				'cheapest dumpster rental near me',
				'residential dumpster sizes',
			],
			openGraph: {
				title,
				description,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title,
				description,
			},
			structuredData,
		}
	},
}

export default function DumpsterSizeCalculatorPage() {
	const { listings, lastUpdated } = useLoaderData<typeof loader>()

	return (
		<div className="space-y-0">
			{/* Calculator — white bg */}
			<div className="py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<DumpsterCalculator listings={listings} />
				</div>
			</div>
			{/* Size guide — muted bg */}
			<div className="bg-muted/30 py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<DumpsterSizeGuide listings={listings} />
				</div>
			</div>
			{/* Vendor table — white bg */}
			<div className="py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<VendorComparisonTable listings={listings} />
				</div>
			</div>
			{/* Permits — muted bg */}
			<div className="bg-muted/30 py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<DumpsterPermitGuide />
				</div>
			</div>
			{/* FAQ — white bg */}
			<div className="py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<DumpsterFaq />
				</div>

				{/* Cross-link to equipment cost estimator */}
				<div className="mx-auto mt-8 max-w-5xl px-4 sm:px-6 lg:px-8">
					<div className="rounded-lg border border-blue-200 bg-blue-50 p-5 dark:border-blue-800 dark:bg-blue-950/30">
						<div className="flex items-start gap-3">
							<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
								<Icon
									name="drill"
									size="sm"
									className="text-blue-600 dark:text-blue-400"
								/>
							</div>
							<div>
								<h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300">
									Need Equipment for Your Project?
								</h3>
								<p className="text-muted-foreground mt-1 text-sm">
									Compare rental costs for 40+ tools and equipment from DFW
									vendors.{' '}
									<Link
										to="/tools/equipment-cost-estimator"
										className="text-primary font-medium hover:underline"
									>
										Use our Equipment & Tool Rental Cost Estimator
									</Link>{' '}
									to find daily, weekly, and monthly rates for everything from
									mini excavators to floor sanders.
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="py-6">
					<p className="text-muted-foreground text-center text-xs">
						Prices and availability updated{' '}
						{new Date(lastUpdated).toLocaleDateString('en-US', {
							month: 'long',
							year: 'numeric',
						})}
						. Actual pricing may vary by vendor and delivery location.
					</p>
				</div>
			</div>
		</div>
	)
}
